import React, { useState } from "react";
import { BCMSDate, BCMSRecord, useBCMSContext, StateManager, BCMSStateManagerCRUD, useStateManager, DisabledPrompt, useRoute, useParams } from "@bcm-dev/bcms-vite";
import Classes from "model";
import FormContainer, { FormButtonContainer, FormRow } from "components/shared/containers/FormContainer";
import MainContentContainer, { MainContentHeader } from "components/shared/containers/MainContentContainer";
import UITextInput from "components/ui/UITextInput";
import CustomSelect, { CustomSelectItem } from "components/ui/CustomSelect";
import PauseCardControlRow from "./components/PauseCardControlRow";
import UIDatePicker from "components/ui/UIDatePicker ";
import UITimeClock from "components/ui/UITimeClock";
import { UICancelButton, UIPrimaryButton } from "components/ui/UIButton";
import dayjs from "dayjs";
import "./styles.css";

/**
 * @function PauseCardControlsTemporarilyPage
 * @return {React.Component}
 * @exported true
 * @description The main component for PauseCardControlsTemporarilyPage
 */

export default function PauseCardControlsTemporarilyPage(props) {
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, globalController);

  const navigate = useRoute();
  const cardID = useParams().cardID ?? "0";
  const data = { ...getDefaultData(), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  const controller = (state, setState) => ({
    data: state.data,
    ui: state.ui,
    onChangeUI: (prop) => (val) => {
      const crud = BCMSStateManagerCRUD.init(state);
      crud.update(["ui", prop], val);
      crud.submit(setState);
    },
    onFormChange: (prop) => (val) => {
      const form = state.data.form;
      const crud = BCMSStateManagerCRUD.init(state);

      if (prop === "temporaryUntilDate") {
        if (val instanceof BCMSDate) {
          crud.update(["data", "form", prop], val);
        } else {
          crud.update(["data", "form", prop], BCMSDate.init(form.temporaryUntilDate));
        }

      } else if (prop === "temporaryUntilTime") {
        if (dayjs(val).isValid()) {
          crud.update(["data", "form", prop], dayjs(val));
        }

      } else {
        crud.update(["data", "form", prop], val);
      }
      crud.submit(setState);
    },
    onCancel: () => navigate(`/home/controlsalerts/controls/${cardID}`)
  });

  return (
    <StateManager state={{ data, ui }} controller={controller}>
      <PageContent />
    </StateManager>
  );
}

/**
 * @function PauseCardControlsTemporarilyView
 * @return {React.Component}
 * @exported true
 * @description Chooses the date and time card controls stay paused.
 */
export function PageContent() {
  const { ui, data, onChangeUI, onFormChange, onCancel } = useStateManager();
  const { showPauseCardForm } = ui ?? {};
  const { form } = data ?? {}
  const isFormFilled = !form.temporaryUntilTime || !form.temporaryUntilDate || !form.applyTo;

  return (
    <MainContentContainer className="pause-card-controls-temporarily-page" header={
      <MainContentHeader dataPoint="Pause_Card_Controls"  text="Pause Card Controls Temporarily" />
    }>
      <FormContainer>
        <FormRow>
          <PauseCardControlRow
            dataPoint="Pause_Card_Controls_Row" 
            onChange={onChangeUI("showPauseCardForm")}
            mainText="Pause Card Controls"
            subText="This temporarily pauses existing card controls. Transactions m..."
            expandedText="This temporarily pauses existing card controls. Transactions may still be declined due to balance limits, fraud checks, network rules and other reasons."
          />
        </FormRow>
        {showPauseCardForm &&
          <div className="pause-card-form-content">
            <FormRow className="temp-until-date-row">
              <UIDatePicker
                dateLabel="Temporary Until (Date)*"
                value={form.temporaryUntilDate}
                onChange={onFormChange("temporaryUntilDate")}
              />
            </FormRow>
            <FormRow className="temp-until-time-row">
              <UITimeClock dateLabel="Temporary Until (Time)*" value={form.temporaryUntilTime} onChange={onFormChange("temporaryUntilTime")} />
            </FormRow>
            <FormRow className="apply-control-pause-to-row">
              <CustomSelect className="apply-control-pause-to-select" label="Apply Control Pause To *" value={form.applyTo} onChange={onFormChange("applyTo")} >
                <CustomSelectItem value="one transaction">One transaction</CustomSelectItem>
                <CustomSelectItem value="all transaction">All transactions</CustomSelectItem>
              </CustomSelect>
            </FormRow>
            <FormRow className="memo-row">
              <UITextInput placeholder="Memo" value={form.memo} onChange={onFormChange("memo")} />
            </FormRow>
          </div>
        }
        <FormButtonContainer>
          <DisabledPrompt>
            <UIPrimaryButton className={`apply-button ${isFormFilled ? "disabled" : ""}`} text="Apply" dataPoint="Apply_Button" />
          </DisabledPrompt>
          <UICancelButton className text="Cancel" onClick={onCancel} dataPoint="Cancel_Button"/>
        </FormButtonContainer>
      </FormContainer>
    </MainContentContainer>
  );
}




export const getDefaultData = () => ({
  form: {
    temporaryUntilDate: BCMSDate.init().offset(7),
    temporaryUntilTime: dayjs().set('hour', 16).set("minute", 0),
    applyTo: "",
    memo: ""
  }
});

export const getDefaultUI = () => ({
  showPauseCardForm: false
});